import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './InputForm.css';

const InputForm = ({ onSubmit, isLoading, disabled }) => {
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [recognitionError, setRecognitionError] = useState(null);
  const [timer, setTimer] = useState(0);
  const [showTimer, setShowTimer] = useState(false);
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setRecognitionError(null);
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        setTextInput(transcript);
      };

      recognition.onerror = (event) => {
        setRecognitionError(`Error: ${event.error}`);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (showTimer && isLoading) {
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (!isLoading) {
        setTimer(0);
        setShowTimer(false);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [showTimer, isLoading]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim()) {
      setShowTimer(true);
      setTimer(0);
      onSubmit({ text: textInput.trim(), image: selectedImage });
    }
  };

  const handleVoiceInput = async () => {
    if (!recognitionRef.current) {
      setRecognitionError('Speech recognition is not available. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (!isRecording) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        setRecognitionError('Failed to start speech recognition. Please try again.');
        setIsRecording(false);
      }
    } else {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        setRecognitionError('Failed to stop speech recognition. Please try again.');
        setIsRecording(false);
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (e) => {
    e.stopPropagation();
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  return (
    <div className={`repair-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="repair-header">
        <h1 className="app-title">
          <span className="title-quick">Quick</span>
          <span className="title-fix">Fix</span>
          <span className="title-ai">AI</span>
        </h1>
        <p className="repair-subtitle">Your AI-powered home maintenance companion</p>
      </div>

      <div className="repair-input-container">
        <div className="input-header">
          <h2>Describe Your Repair Issue</h2>
          <p>Tell us what needs fixing, and we'll provide you with a detailed repair guide</p>
        </div>

        <form onSubmit={handleTextSubmit} className="unified-input-form">
          <div className="input-sections-container">
            <div className="text-input-section">
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="For example: My kitchen sink is leaking from under the cabinet. I can see water pooling on the floor..."
                rows={4}
                disabled={isLoading || disabled}
              />
              {recognitionError && (
                <div className="recognition-error">
                  {recognitionError}
                </div>
              )}
              <div className="voice-input-button">
                <button
                  type="button"
                  className={isRecording ? 'recording' : ''}
                  onClick={handleVoiceInput}
                  disabled={isLoading || disabled}
                >
                  {isRecording ? (
                    <>
                      <span className="recording-dot"></span>
                      Recording...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                      Record Voice
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="image-upload-section">
              <div className="image-upload-header">
                <h3>Add a Photo (Optional)</h3>
                <p>Upload a photo of the issue for better analysis</p>
              </div>
              <div 
                className="image-upload-area"
                onClick={(e) => !selectedImage && handleUploadClick(e)}
              >
                {selectedImage ? (
                  <div className="image-preview-container">
                    <img src={selectedImage} alt="Upload preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={removeImage}
                      disabled={isLoading || disabled}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={isLoading || disabled}
                    />
                    <button
                      type="button"
                      className="upload-button"
                      onClick={handleUploadClick}
                      disabled={isLoading || disabled}
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload Photo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="submit-button-container">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isLoading || disabled || !textInput.trim()}
            >
              {isLoading ? (
                <div className="timer-container">
                  <span>Processing...</span>
                  {showTimer && (
                    <span className="timer-display">{formatTime(timer)}</span>
                  )}
                </div>
              ) : (
                'Get Repair Guide'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InputForm; 